import { Box, Typography, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useGoalDetailPortfoliosQuery } from "web_myplann/Api/Goals/useGoalDetailPortfoliosQuery";
import { GoalDetailSolutionItemSkeleton } from "web_myplann/Goals/Components/GoalDetailSolutionItemSkeleton";
import { PinExistingContractItem } from "web_myplann/Goals/Components/PinContract/PinExistingContractItem";
import type {
	GoalPortfolioListItemResponseDTO,
	GoalPortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

type Props = {
	open: boolean;
	onClose: () => void;
	onSelect: (portfolio: GoalPortfolioListItemResponseDTO) => void;
	onAddNew: () => void;
	goalPortfolios: GoalPortfolioResponseDTO[];
};

export const PinExistingContractDrawer: FC<Props> = ({
	open,
	onClose,
	onSelect,
	onAddNew,
	goalPortfolios,
}) => {
	const { spacingTokens } = useTheme();
	const { t } = useResource();
	const { data, isPending, isSuccess } = useGoalDetailPortfoliosQuery();

	const portfolios = useMemo(() => data?.data ?? [], [data]);
	const pinnedById = useMemo(
		() =>
			new Map(goalPortfolios.map((gp) => [gp.portfolioId, gp.usagePercent])),
		[goalPortfolios],
	);

	const isEmpty = isSuccess && portfolios.length === 0;

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(Resources.Goals.Detail.Solution.Pin.Existing.title)}
			closeButton
		>
			<Box pb={spacingTokens.lg}>
				{isPending ? (
					<>
						<GoalDetailSolutionItemSkeleton hasDivider />
						<GoalDetailSolutionItemSkeleton hasDivider={false} />
					</>
				) : isEmpty ? (
					<Box px={spacingTokens.lg} py={spacingTokens.lg} textAlign="center">
						<Typography variant="body2">
							{t(Resources.Goals.Detail.Solution.Pin.Existing.noEligible)}
						</Typography>
					</Box>
				) : (
					<>
						<Typography
							variant="hint"
							display="block"
							px={spacingTokens.lg}
							pb={spacingTokens.md}
						>
							{t(Resources.Goals.Detail.Solution.Pin.Existing.hint)}
						</Typography>
						{portfolios.map((portfolio, portfolioIndex) => (
							<PinExistingContractItem
								key={portfolio.id}
								portfolio={portfolio}
								pinnedAllocation={pinnedById.get(portfolio.id) ?? null}
								hasDivider={portfolioIndex < portfolios.length - 1}
								onSelect={onSelect}
							/>
						))}
					</>
				)}
				<AddRowButton
					label={t(Resources.Goals.Detail.Solution.Pin.Existing.addNew)}
					onClick={onAddNew}
				/>
			</Box>
		</MyPlannDrawer>
	);
};
